/**
 * chat-director/select — pure speaker + beat selection.
 *
 * From current beats + a roster of available speakers, decide WHO speaks about
 * WHICH beats. Deterministic: all randomness flows through an injected core Rng,
 * so the same (beats, roster, rng-seed) always yields the same plan. The voice
 * layer only PHRASES the chosen beats; selection never invents canon.
 */
import type { Beat, BeatPriority, ChatChannel, SpeakerContext } from '@/shared';
import type { Rng } from '@/core/rng.js';
import { threadBeats } from './thread.js';

/** How many lines a beat of each priority is "worth" in a batch. */
export const PRIORITY_LINE_WEIGHT: Readonly<Record<BeatPriority, number>> = {
  ambient: 1,
  normal: 2,
  highlight: 3,
};

/** Max beats folded into a single batched exchange (keeps the call short). */
export const MAX_BEATS_PER_BATCH = 4;
/** Max distinct speakers in one exchange (a believable back-and-forth, not a mob). */
export const MAX_SPEAKERS_PER_BATCH = 4;
/** Hard ceiling on lines per exchange so a batch reads at a calm pace. */
export const MAX_LINES_PER_BATCH = 6;

/** A coherent group of beats + chosen speakers destined for one VoiceRequest. */
export interface SpeakerPlan {
  readonly channel: ChatChannel;
  readonly beats: readonly Beat[];
  readonly speakers: readonly SpeakerContext[];
  readonly desiredLineCount: number;
}

const PRIORITY_RANK: Readonly<Record<BeatPriority, number>> = {
  highlight: 0,
  normal: 1,
  ambient: 2,
};

/** Order beats loudest-first, then freshest-first (bias toward fresh canon). */
export function orderBeats(beats: readonly Beat[]): Beat[] {
  return beats.slice().sort((a, b) => {
    const pr = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (pr !== 0) return pr;
    if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

/**
 * Group ordered beats for a channel into batch-sized chunks, each a coherent
 * multi-turn topic of back-and-forth (a show-off + the admiration it earns, a
 * question + its answer) rather than a stack of unrelated one-liners.
 *
 * Grouping keeps exchanges coherent AND keeps budget degradation meaningful:
 *  - Beats are bucketed by PRIORITY tier first, so a batch is tonally
 *    homogeneous (ambient banter never rides on a highlight's coat-tails). This
 *    is what lets the budget layer degrade breadth tier-by-tier — drop ambient
 *    batches to templates while highlight batches keep a fresh voice.
 *  - Within a tier, beats are THREADED by what they're about (a trainer's show-off
 *    and a peer's admiration of that same mon thread together; the player's mon and
 *    the warm reactions to it thread together) so an exchange reads as a real
 *    conversation. Same-subject beats fall into the same event-thread, preserving
 *    the old subject-clustering behavior.
 *
 * A batch never exceeds MAX_BEATS_PER_BATCH so the exchange stays short +
 * affordable; a thread larger than that splits across batches. Output batch order
 * is loudest-tier-first (matches orderBeats).
 */
export function groupBeats(beats: readonly Beat[]): Beat[][] {
  const ordered = orderBeats(beats);

  // Bucket by priority tier, preserving first-seen order within each tier.
  const byTier = new Map<BeatPriority, Beat[]>();
  for (const beat of ordered) {
    let tier = byTier.get(beat.priority);
    if (!tier) {
      tier = [];
      byTier.set(beat.priority, tier);
    }
    tier.push(beat);
  }

  const batches: Beat[][] = [];
  for (const priority of ['highlight', 'normal', 'ambient'] as const) {
    const tier = byTier.get(priority);
    if (!tier) continue;

    // Thread the tier into coherent topics, then emit each as a batch (splitting
    // only when a single thread overflows the batch cap).
    let current: Beat[] = [];
    const flush = (): void => {
      if (current.length > 0) {
        batches.push(current);
        current = [];
      }
    };
    for (const thread of threadBeats(tier)) {
      // A fresh thread starts a fresh batch so topics don't bleed together; small
      // threads that fit alongside the current one are merged to keep batches full.
      if (current.length > 0 && current.length + thread.beats.length > MAX_BEATS_PER_BATCH) {
        flush();
      }
      for (const beat of thread.beats) {
        if (current.length >= MAX_BEATS_PER_BATCH) flush();
        current.push(beat);
      }
    }
    flush();
  }
  return batches;
}

/** Sum the per-priority line weight of a batch, clamped to MAX_LINES_PER_BATCH. */
export function desiredLineCount(beats: readonly Beat[]): number {
  const raw = beats.reduce((sum, b) => sum + PRIORITY_LINE_WEIGHT[b.priority], 0);
  return Math.max(1, Math.min(MAX_LINES_PER_BATCH, raw));
}

/** True if any beat in the batch is player-started or about the player (two-way). */
function involvesPlayer(beats: readonly Beat[]): boolean {
  return beats.some(
    (b) =>
      b.subjectTrainerId === 'player' ||
      (b.involvedTrainerIds ?? []).includes('player') ||
      b.data.byPlayer === true ||
      b.data.aboutPlayerMon === true,
  );
}

/**
 * The line count for a multi-turn exchange: at least the priority-weighted count,
 * but bumped so a coherent thread reads as genuine back-and-forth — never a lone
 * one-liner when there's a conversation to have. A player-involved thread always
 * earns a real reply (≥2 lines) so the world talks WITH the player, not at them.
 */
export function conversationalLineCount(beats: readonly Beat[], speakerCount: number): number {
  const base = desiredLineCount(beats);
  let lines = Math.max(base, speakerCount);
  // A thread spanning multiple speakers should banter back and forth, not just
  // emit one line each — give it room for a reply round.
  if (speakerCount >= 2) lines = Math.max(lines, speakerCount + 1);
  // The player showing off / chatting always gets a warm multi-voice response.
  if (involvesPlayer(beats)) lines = Math.max(lines, 2);
  return Math.min(MAX_LINES_PER_BATCH, lines);
}

/**
 * Pick the speakers for one batch of beats.
 *
 * The trainers a beat is *about* (subject + involved) speak first when present in
 * the roster; remaining slots are filled by chattier regulars to react/banter.
 * Selection is deterministic given the rng. Returns speakers in a stable order.
 */
export function selectSpeakers(
  beats: readonly Beat[],
  roster: readonly SpeakerContext[],
  rng: Rng,
): SpeakerContext[] {
  const byId = new Map(roster.map((s) => [s.trainerId, s]));
  const chosen: SpeakerContext[] = [];
  const taken = new Set<string>();

  const take = (id: string): void => {
    if (taken.has(id)) return;
    const sp = byId.get(id);
    if (!sp) return;
    if (chosen.length >= MAX_SPEAKERS_PER_BATCH) return;
    chosen.push(sp);
    taken.add(id);
  };

  // 1) The principals: who the beats are about.
  for (const beat of beats) {
    take(beat.subjectTrainerId);
    for (const other of beat.involvedTrainerIds ?? []) take(other);
  }

  // 2) Fill remaining slots with chattier regulars (warm, ambient reactions).
  if (chosen.length < MAX_SPEAKERS_PER_BATCH) {
    const rest = roster.filter((s) => !taken.has(s.trainerId));
    // Shuffle for variety, then bias toward chattier voices (stable sort keeps
    // the shuffled order among equal chattiness — fresh yet deterministic).
    const shuffled = rng.shuffle(rest);
    shuffled.sort((a, b) => chattiness(b) - chattiness(a));
    for (const sp of shuffled) {
      if (chosen.length >= MAX_SPEAKERS_PER_BATCH) break;
      take(sp.trainerId);
    }
  }

  return chosen;
}

/**
 * Crude chattiness proxy from the persona summary + friendship: warmer, more
 * familiar trainers are likelier to chime in. SpeakerContext carries no numeric
 * chattiness, so we approximate from what the contract exposes.
 */
function chattiness(s: SpeakerContext): number {
  const summary = s.personaSummary.toLowerCase();
  let score = s.friendshipWithPlayer / 100;
  if (/(chatty|talkative|bubbly|social|outgoing|cheer)/.test(summary)) score += 1;
  if (/(quiet|shy|reserved|gruff|terse)/.test(summary)) score -= 0.5;
  if (s.memorySnippets.length > 0) score += 0.1;
  return score;
}

/**
 * Build the full set of speaker plans for a channel: group the beats, then pick
 * speakers per group. Batches with no resolvable speaker are dropped (nothing to
 * voice). Pure given the rng.
 */
export function planSpeakers(
  channel: ChatChannel,
  beats: readonly Beat[],
  roster: readonly SpeakerContext[],
  rng: Rng,
): SpeakerPlan[] {
  const plans: SpeakerPlan[] = [];
  for (const group of groupBeats(beats)) {
    const speakers = selectSpeakers(group, roster, rng);
    if (speakers.length === 0) continue;
    plans.push({
      channel,
      beats: group,
      speakers,
      desiredLineCount: conversationalLineCount(group, speakers.length),
    });
  }
  return plans;
}
